import { cx } from '../styles/theme';
import { RAMP_RESIDUAL_LABEL } from '../styles/chartRamp';

// One labelled slice of a composition bar. `pct` is the share of the whole
// (0–100); `amount` is a pre-formatted display string (e.g. "$1,234") so the
// caller controls rounding/reconciliation. `color` is a ramp colour (a CSS
// `var()`). `residual` marks the trailing "other"/"N more projects" slice,
// which takes a muted navy label (§4).
export interface CompositionSlice {
  label: string;
  pct: number;
  amount: string;
  color: string;
  residual?: boolean;
}

export interface CompositionBarProps {
  title: string; // "By model" / "By project"
  slices: CompositionSlice[];
}

// A share below 1% must read "<1%", never "0%" (#630 §4) — a 0.4%-of-spend
// model is still a real line item.
function formatPct(pct: number): string {
  if (pct > 0 && pct < 1) return '<1%';
  return `${Math.round(pct)}%`;
}

/**
 * CompositionBar (#630 §4) — a 12px pill-radius stacked bar over a legend grid.
 * Both spend breakdowns (by model, by project) render through this one
 * primitive off the same ramp, so they are directly comparable.
 */
export function CompositionBar({ title, slices }: CompositionBarProps) {
  return (
    <div>
      <div className="mb-2 text-xs font-medium uppercase tracking-wider text-navy-400">
        {title}
      </div>

      {/* Empty range (activity but no priced spend — subscription users,
          unpriced periods): a blank pill + empty legend reads as broken, so
          show an explicit "No data" line instead. */}
      {slices.length === 0 ? (
        <div className="font-mono text-sm text-navy-400">No data</div>
      ) : (
        <>
      {/* Track: 12px pill, overflow hidden so segments clip to the radius. */}
      <div
        className="flex overflow-hidden"
        style={{
          height: 12,
          borderRadius: 'var(--radius-pill, 9999px)',
          background: 'var(--color-gray-100)',
        }}
        role="img"
        aria-label={title}
      >
        {slices.map((s, i) => (
          <div
            key={`${s.label}-${i}`}
            style={{ width: `${s.pct}%`, background: s.color }}
          />
        ))}
      </div>

      {/* Legend: swatch · label · percent · amount. */}
      <div
        className="mt-3"
        style={{
          display: 'grid',
          gridTemplateColumns: '12px minmax(0,1fr) auto auto',
          columnGap: 10,
          rowGap: 9,
          alignItems: 'center',
        }}
      >
        {slices.map((s, i) => (
          <div key={`${s.label}-${i}`} style={{ display: 'contents' }}>
            <span
              aria-hidden
              style={{ width: 9, height: 9, borderRadius: 2, background: s.color }}
            />
            <span
              className={cx(
                'truncate font-mono text-sm',
                s.residual ? '' : 'text-navy-700 dark:text-navy-300',
              )}
              style={s.residual ? { color: RAMP_RESIDUAL_LABEL } : undefined}
            >
              {s.label}
            </span>
            <span
              className="text-right font-mono text-sm tabular-nums text-navy-400"
              style={{ minWidth: 38 }}
            >
              {formatPct(s.pct)}
            </span>
            <span
              className="text-right font-mono text-sm tabular-nums text-navy-900 dark:text-white"
              style={{ minWidth: 66 }}
            >
              {s.amount}
            </span>
          </div>
        ))}
      </div>
        </>
      )}
    </div>
  );
}
